import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, Button, Table, useTable } from "@repo/o37-ui";
import { Plus, CreditCard, ChevronRight, Users, AlertCircle, TrendingUp } from "lucide-react";
import {
  HoldingsSubscription,
  HoldingsAlert,
  HOLDINGS_SUBSCRIPTION_STATUSES,
  HOLDINGS_BILLING_CYCLES,
  formatCents,
  getMonthlyEquivalent
} from '../../types';
import SubscriptionForm from '../../components/Holdings/SubscriptionForm';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

interface SubscriptionListProps {
  onDataChange?: () => void;
  subscriptionAlerts?: Map<string, HoldingsAlert[]>;
}

interface SubscriptionWithAlerts extends HoldingsSubscription {
  alerts?: HoldingsAlert[];
}

export default function SubscriptionList({ onDataChange, subscriptionAlerts }: SubscriptionListProps) {
  const [subscriptions, setSubscriptions] = useState<SubscriptionWithAlerts[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });
  const [showForm, setShowForm] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState<HoldingsSubscription | null>(null);
  const [showOnlyWithAlerts, setShowOnlyWithAlerts] = useState(false);

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadSubscriptions();
  }, []);

  // Attach alerts to subscriptions when either changes
  useEffect(() => {
    if (subscriptionAlerts) {
      setSubscriptions(prev => prev.map(s => ({
        ...s,
        alerts: subscriptionAlerts.get(s.id) || []
      })));
    }
  }, [subscriptionAlerts]);

  const loadSubscriptions = async () => {
    try {
      const response = await fetch(`${API_URL}/holdings/subscriptions`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        const transformed: SubscriptionWithAlerts[] = data.map((s: any) => ({
          id: s.id,
          vendorName: s.vendor_name,
          description: s.description,
          costCents: s.cost_cents,
          billingCycle: s.billing_cycle,
          renewalDate: s.renewal_date,
          startDate: s.start_date,
          totalSeats: s.total_seats || 0,
          category: s.category,
          status: s.status,
          autoRenew: s.auto_renew,
          contractUrl: s.contract_url,
          notes: s.notes,
          createdBy: s.created_by,
          createdAt: s.created_at,
          updatedAt: s.updated_at,
          assignedSeats: s.assigned_seats || 0,
          unassignedSeats: (s.total_seats || 0) - (s.assigned_seats || 0),
          lastChargedAmountCents: s.last_charged_amount_cents,
          lastChargedDate: s.last_charged_date,
          alerts: subscriptionAlerts?.get(s.id) || []
        }));
        // Sort: active first, then by vendor name
        transformed.sort((a, b) => {
          if (a.status === 'active' && b.status !== 'active') return -1;
          if (a.status !== 'active' && b.status === 'active') return 1;
          return a.vendorName.localeCompare(b.vendorName);
        });
        setSubscriptions(transformed);
      }
    } catch (e) {
      console.error('Failed to load subscriptions:', e);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const found = HOLDINGS_SUBSCRIPTION_STATUSES.find(s => s.status === status);
    return found?.color || 'bg-neutral-100 text-neutral-500';
  };

  const getBillingLabel = (cycle: string) => {
    const found = HOLDINGS_BILLING_CYCLES.find(c => c.cycle === cycle);
    return found?.label || cycle;
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getAlertBadge = (alerts: HoldingsAlert[] | undefined) => {
    if (!alerts || alerts.length === 0) return null;

    const priceChangeAlerts = alerts.filter(a => a.alertType === 'price_change');
    const reviewAlerts = alerts.filter(a => a.alertType === 'match_review');

    if (priceChangeAlerts.length > 0) {
      return (
        <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-medium">
          <TrendingUp className="w-3 h-3" />
          Price
        </span>
      );
    }

    if (reviewAlerts.length > 0) {
      return (
        <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">
          <AlertCircle className="w-3 h-3" />
          Review
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-medium">
        <AlertCircle className="w-3 h-3" />
        {alerts.length}
      </span>
    );
  };

  const filteredSubscriptions = showOnlyWithAlerts
    ? subscriptions.filter(s => s.alerts && s.alerts.length > 0)
    : subscriptions;

  const subscriptionsWithAlerts = subscriptions.filter(s => s.alerts && s.alerts.length > 0);

  const columns = useMemo(() => [
    {
      id: "vendor",
      header: "Subscription",
      accessorFn: (row: SubscriptionWithAlerts) => row.vendorName,
      cell: ({ row }: { row: { original: SubscriptionWithAlerts } }) => (
        <Link to={`/holdings/costs/subscriptions/${row.original.id}`} className="group block py-1">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="font-medium text-neutral-900 group-hover:underline decoration-neutral-400 decoration-dotted underline-offset-2">
              {row.original.vendorName}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusColor(row.original.status)}`}>
              {row.original.status.charAt(0).toUpperCase() + row.original.status.slice(1)}
            </span>
            {getAlertBadge(row.original.alerts)}
          </div>
          {row.original.description && (
            <div className="text-sm text-neutral-500 truncate max-w-xs">{row.original.description}</div>
          )}
        </Link>
      )
    },
    {
      id: "cost",
      header: "Cost",
      accessorFn: (row: SubscriptionWithAlerts) => row.costCents,
      cell: ({ row }: { row: { original: SubscriptionWithAlerts } }) => {
        const hasLastCharge = row.original.lastChargedAmountCents && row.original.lastChargedAmountCents !== row.original.costCents;

        return (
          <div>
            <div className="font-medium text-neutral-900">
              {formatCents(row.original.costCents)}
            </div>
            <div className="text-xs text-neutral-500">
              /{getBillingLabel(row.original.billingCycle).toLowerCase()}
            </div>
            {hasLastCharge && (
              <div className="text-xs text-amber-600 mt-0.5">
                Last: {formatCents(row.original.lastChargedAmountCents!)}
              </div>
            )}
          </div>
        );
      }
    },
    {
      id: "monthly",
      header: "Monthly",
      accessorFn: (row: SubscriptionWithAlerts) => getMonthlyEquivalent(row.costCents, row.billingCycle),
      cell: ({ row }: { row: { original: SubscriptionWithAlerts } }) => (
        <span className="text-neutral-600">
          {formatCents(getMonthlyEquivalent(row.original.costCents, row.original.billingCycle))}
        </span>
      )
    },
    {
      id: "seats",
      header: "Seats",
      cell: ({ row }: { row: { original: SubscriptionWithAlerts } }) => {
        const total = row.original.totalSeats;
        const assigned = row.original.assignedSeats || 0;
        if (total === 0) return <span className="text-neutral-400">-</span>;
        return (
          <div className="flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5 text-neutral-400" />
            <span className={assigned === total ? 'text-emerald-600' : 'text-neutral-600'}>
              {assigned}/{total}
            </span>
          </div>
        );
      }
    },
    {
      id: "category",
      header: "Category",
      accessorFn: (row: SubscriptionWithAlerts) => row.category,
      cell: ({ row }: { row: { original: SubscriptionWithAlerts } }) => (
        <span className="text-neutral-500 text-sm">
          {row.original.category || '-'}
        </span>
      )
    },
    {
      id: "renewal",
      header: "Renewal",
      accessorFn: (row: SubscriptionWithAlerts) => row.renewalDate,
      cell: ({ row }: { row: { original: SubscriptionWithAlerts } }) => (
        <span className="text-neutral-500 text-sm">
          {formatDate(row.original.renewalDate)}
        </span>
      )
    },
    {
      id: "actions",
      header: "",
      size: 50,
      cell: () => (
        <div className="flex justify-end">
          <ChevronRight className="w-4 h-4 text-neutral-400" />
        </div>
      )
    }
  ], []);

  const { table, ...tableProps } = useTable({
    data: filteredSubscriptions,
    columns,
    pagination,
    onPaginationChange: setPagination,
    rowCount: filteredSubscriptions.length,
    resourceName: (plural) => `subscription${plural ? "s" : ""}`,
  });

  const handleFormClose = () => {
    setShowForm(false);
    setEditingSubscription(null);
  };

  const handleFormSaved = () => {
    handleFormClose();
    loadSubscriptions();
    onDataChange?.();
  };

  if (loading) {
    return <p className="text-neutral-500">Loading subscriptions...</p>;
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="text-sm text-neutral-500">
            {subscriptions.length} subscription{subscriptions.length !== 1 ? 's' : ''}
          </div>
          {subscriptionsWithAlerts.length > 0 && (
            <button
              onClick={() => setShowOnlyWithAlerts(!showOnlyWithAlerts)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${
                showOnlyWithAlerts
                  ? 'bg-amber-500 text-white'
                  : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
              }`}
            >
              <span className="flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {subscriptionsWithAlerts.length} with issues
              </span>
            </button>
          )}
        </div>
        <Button
          onClick={() => setShowForm(true)}
          icon={<Plus className="w-4 h-4" />}
          text="Add Subscription"
        />
      </div>

      {/* Table */}
      {filteredSubscriptions.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <CreditCard className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {showOnlyWithAlerts ? 'No Subscriptions With Issues' : 'No Subscriptions Yet'}
            </h3>
            <p className="text-neutral-500 mb-6">
              {showOnlyWithAlerts
                ? 'All subscriptions are in good standing.'
                : 'Add your first subscription to start tracking costs.'}
            </p>
            {!showOnlyWithAlerts && (
              <Button
                onClick={() => setShowForm(true)}
                text="Add Subscription"
              />
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table {...tableProps} table={table} />
        </Card>
      )}

      {/* Subscription Form Modal */}
      {showForm && (
        <SubscriptionForm
          subscription={editingSubscription}
          onClose={handleFormClose}
          onSaved={handleFormSaved}
        />
      )}
    </div>
  );
}
